// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { ApiOutlined } from '@ant-design/icons';
import { antGrey } from '../../styles/globalStyles';
import {
    AppComponentProps,
    CAPABILITY_URL_PARAM,
    SetupStep,
    SetupStepsCallerContext,
} from '../../../typescriptHelpers';
import { IntegrationCapability, IntegrationType } from '../../../types';
import {
    selectAppNeedsIntegrationsSetup,
    selectIntegrationFromIntegrationType,
    selectIncompleteCapabilitiesForApp,
} from '../../../selectors/selectors';
import { AutomationTip, HelpTip, SecureTip, setupModuleStyle } from '../shared';
import { CapabilitySection } from '../CapabilitySection';
import { useAddIntegrationModal } from '../useAddIntegrationModal';
import { useUpdateIntegrationModal } from '../useUpdateIntegrationModal';
import ConnectIntegrationModal from '../../ConnectIntegrationModal';
import IntegrationProviderModal from '../../IntegrationProviderModal';
import IntegrationConfiguration from '../../IntegrationConfiguration';
import { useHistory } from 'react-router-dom';

const SetupViewComponent: React.FC<IntegrationSetupViewComponentProps> = ({ appId, orgId, context }) => {
    const history = useHistory();

    const capability = React.useMemo(() => {
        const params = new URLSearchParams(history.location.search);
        const possibleCapability = params.get(CAPABILITY_URL_PARAM);
        return Object.values(IntegrationCapability).find((c) => c === possibleCapability);
    }, [history.location.search]);

    return <IntegrationsSetupViewComponent appId={appId} orgId={orgId} context={context} capability={capability} />;
};

interface IntegrationSetupViewComponentProps extends AppComponentProps {
    capability?: IntegrationCapability;
    onCompleteIntegrationSetup?: () => void;
    context?: SetupStepsCallerContext;
}

export const IntegrationsSetupViewComponent: React.FC<IntegrationSetupViewComponentProps> = ({
    appId,
    orgId,
    capability,
    onCompleteIntegrationSetup,
    context,
}) => {
    const [integrationType, setIntegrationType] = React.useState(null);
    const selectedIntegration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, integrationType),
    );

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        onUpdateIntegrationSuccess: onCompleteIntegrationSetup,
    });

    //handles modal for new integration
    const {
        setShowIntegrationModal: setShowConnectIntegrationModal,
        integrationModalProps: connectIntegrationModalProps,
    } = useAddIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        integrationType,
        setShowUpdateIntegrationModal,
    });

    //handles modal for existing integration installation
    const {
        setShowIntegrationModal: setShowIntegrationProviderModal,
        integrationModalProps: integrationProviderModalProps,
    } = useAddIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        integrationType,
        setShowUpdateIntegrationModal,
    });

    const onClickIntegrationShowInstallationModal = (integrationType: IntegrationType) => {
        setIntegrationType(integrationType);
        setShowConnectIntegrationModal(true);
    };

    const onClickShowProviderModal = (integrationType: IntegrationType) => {
        setIntegrationType(integrationType);
        setShowIntegrationProviderModal(true);
    };

    const incompleteCapabilities = useSelector((state: RootState) =>
        selectIncompleteCapabilitiesForApp(state, orgId, appId),
    );
    const allIntegrationsComplete = incompleteCapabilities.length === 0;

    return (
        <div style={{ ...setupModuleStyle }}>
            {allIntegrationsComplete && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                    <Typography.Text style={{ fontSize: 24, fontWeight: 600, color: antGrey.grey7 }}>
                        Nice, you’ve set up all your integrations!
                    </Typography.Text>
                </div>
            )}
            {capability ?
                <CapabilitySection
                    shouldShowTitle
                    capability={capability}
                    addIntegration={connectIntegrationModalProps.addIntegration}
                    onClickIntegrationShowInstallationModal={onClickIntegrationShowInstallationModal}
                    onClickShowProviderModal={onClickShowProviderModal}
                    appId={appId}
                    orgId={orgId}
                    context={context}
                />
            :   incompleteCapabilities.map((capability, idx) => {
                    return (
                        <React.Fragment key={idx}>
                            <CapabilitySection
                                shouldShowTitle
                                capability={capability}
                                addIntegration={(...args) => {
                                    const integrationType = args[0];
                                    setIntegrationType(integrationType);
                                    connectIntegrationModalProps.addIntegration(...args);
                                }}
                                onClickIntegrationShowInstallationModal={onClickIntegrationShowInstallationModal}
                                onClickShowProviderModal={onClickShowProviderModal}
                                appId={appId}
                                orgId={orgId}
                                context={context}
                            />
                            {idx !== incompleteCapabilities.length - 1 && (
                                <Divider type="horizontal" style={{ borderColor: antGrey.grey6, margin: '48px 0px' }} />
                            )}
                        </React.Fragment>
                    );
                })
            }
            <ConnectIntegrationModal {...connectIntegrationModalProps} appId={appId} />
            <IntegrationProviderModal
                {...integrationProviderModalProps}
                onConnectNewProvider={(integrationType) => {
                    setShowIntegrationProviderModal(false);
                    onClickIntegrationShowInstallationModal(integrationType);
                }}
            />
            {selectedIntegration && (
                <IntegrationConfiguration
                    {...updateIntegrationModalProps}
                    onClose={() => {
                        setShowUpdateIntegrationModal(false);
                    }}
                />
            )}
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <AutomationTip>
                More connected integrations means more that Runway can automate (and more hours saved for your team!).
            </AutomationTip>
            <SecureTip>
                All integrations are connected via official APIs, and authenticated with the most modern option offered.
                We are SOC 2 certified.
            </SecureTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const KeyIntegrationsStep: SetupStep = {
    title: 'Get more out of Runway.',
    description: () => {
        return 'Connect more integrations to make Runway your team’s complete mobile control center.';
    },
    icon: () => ApiOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Connect more integrations',
    listDescription: 'Get more out of Runway.',
    integrationsRecommendationBannerData: null,
    isCompleteSelector: (root, { appId }) => {
        const appNeedsIntegrationSetup = selectAppNeedsIntegrationsSetup(root, appId);
        return !appNeedsIntegrationSetup;
    },
};
